import {
  api,
  ApiError,
  type LimityWgrywaniaDto,
  type OdpowiedzPorcjiDto,
  type OdpowiedzPrzyjecia,
  type OdpowiedzStartuWgrywania,
  type PolaStartuWgrywania,
  type StanWgrywaniaDto,
  type WejscieNagrania,
} from "./api.js";

/**
 * Wgrywanie nagrania: mala rzecz jednym zadaniem (stara sciezka), duza porcjami z wznowieniem.
 *
 * Rdzen jest bez przegladarki: siec (transport), zegar (`teraz`) i czekanie miedzy ponowieniami (`czekaj`) sa
 * wstrzykiwane, wiec testy przechodza caly harmonogram ponowien bez jednej sekundy realnego czekania
 * (tests/dom/wgrywanie.test.ts). Domyslny transport to `api` z lib/api.ts, brany leniwie przy kazdym wywolaniu -
 * dzieki temu testy komponentow, ktore podmieniaja `api`, nie wymagaja osobnej atrapy.
 *
 * Zasada projektu: nie pokazujemy liczby, ktorej nie ufamy. Predkosc i czas do konca pojawiaja sie dopiero, gdy jest
 * z czego je uczciwie policzyc (patrz `szacujPredkosc`), a do tego czasu postep to same potwierdzone bajty.
 */

// ---------------------------------------------------------------------------
// Limity
// ---------------------------------------------------------------------------

export interface LimityWgrywania {
  maxBajtowNagrania: number;
  /** null: serwer nie podaje limitu czasu (starszy serwer), wiec go nie pokazujemy i nie sprawdzamy. */
  maxCzasuNagraniaSek: number | null;
  /** null: starszy serwer, porcji nie ma. Rozmiar porcji i tak podaje serwer przy zakladaniu wgrywania. */
  rozmiarPorcjiBajtow: number | null;
  maxBajtowJednegoZadania: number;
}

export interface WynikLimitow {
  /** "stary_serwer": trasy limitow nie ma, dzialamy jak przed wprowadzeniem wgrywania porcjami. */
  zrodlo: "serwer" | "stary_serwer";
  limity: LimityWgrywania;
}

/**
 * Limit starszego serwera: jedno zadanie multipart i UPLOAD_MAX_BYTES z jego env.ts (200 MiB). To LUSTRO wartosci
 * domyslnej, uzywane tylko wtedy, gdy serwer nie ma trasy limitow - rozstrzyga i tak serwer, a jego komunikat
 * pokazujemy, gdy plik odbije sie o realny limit.
 */
export const LIMIT_STAREGO_SERWERA_BAJTOW = 200 * 1024 * 1024;

const LIMITY_STAREGO_SERWERA: LimityWgrywania = {
  maxBajtowNagrania: LIMIT_STAREGO_SERWERA_BAJTOW,
  maxCzasuNagraniaSek: null,
  rozmiarPorcjiBajtow: null,
  maxBajtowJednegoZadania: LIMIT_STAREGO_SERWERA_BAJTOW,
};

function dodatniaLiczba(wartosc: unknown): wartosc is number {
  return typeof wartosc === "number" && Number.isFinite(wartosc) && wartosc > 0;
}

/** Odpowiedz serwera tylko w calosci albo wcale: polowiczne limity prowadzilyby do zgadywania reszty. */
function limityZOdpowiedzi(dto: Partial<LimityWgrywaniaDto> | null | undefined): LimityWgrywania | null {
  if (!dto) return null;
  if (
    !dodatniaLiczba(dto.maxBajtowNagrania) ||
    !dodatniaLiczba(dto.maxCzasuNagraniaSek) ||
    !dodatniaLiczba(dto.rozmiarPorcjiBajtow) ||
    !dodatniaLiczba(dto.maxBajtowJednegoZadania)
  ) {
    return null;
  }
  return {
    maxBajtowNagrania: dto.maxBajtowNagrania,
    maxCzasuNagraniaSek: dto.maxCzasuNagraniaSek,
    rozmiarPorcjiBajtow: dto.rozmiarPorcjiBajtow,
    maxBajtowJednegoZadania: dto.maxBajtowJednegoZadania,
  };
}

/** 404/405/501: serwer nie zna trasy. Inne bledy (siec, 401, 5xx) sa przejsciowe i NIE znacza "starszy serwer". */
function brakTrasyLimitow(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 405 || err.status === 501);
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

/** Wszystko, czego wgrywanie potrzebuje od sieci. Domyslna implementacja deleguje do `api`. */
export interface TransportWgrywania {
  limity(signal?: AbortSignal): Promise<LimityWgrywaniaDto>;
  /** Stara sciezka: caly plik jednym zadaniem multipart. */
  jednoZadanie(wejscie: WejscieNagrania, signal?: AbortSignal): Promise<OdpowiedzPrzyjecia>;
  rozpocznij(pola: PolaStartuWgrywania, signal?: AbortSignal): Promise<OdpowiedzStartuWgrywania>;
  stan(uploadId: string, signal?: AbortSignal): Promise<StanWgrywaniaDto>;
  porcja(uploadId: string, index: number, dane: Blob, signal?: AbortSignal): Promise<OdpowiedzPorcjiDto>;
  zakoncz(uploadId: string): Promise<OdpowiedzPrzyjecia>;
  anuluj(uploadId: string): Promise<void>;
}

export const TRANSPORT_API: TransportWgrywania = {
  limity: (signal) => api.limityWgrywania(signal),
  jednoZadanie: (wejscie, signal) => api.upload(wejscie, { signal }),
  rozpocznij: (pola, signal) => api.wgrywanieStart(pola, signal),
  stan: (uploadId, signal) => api.wgrywanieStan(uploadId, signal),
  porcja: (uploadId, index, dane, signal) => api.wgrywaniePorcja(uploadId, index, dane, signal),
  zakoncz: (uploadId) => api.wgrywanieKoniec(uploadId),
  anuluj: (uploadId) => api.wgrywanieAnuluj(uploadId),
};

// ---------------------------------------------------------------------------
// Limity: pobranie z pamiecia
// ---------------------------------------------------------------------------

let pamiecLimitow = new WeakMap<TransportWgrywania, Promise<WynikLimitow>>();

/** Dla testow: zapomina zapamietane limity (modul zyje tak dlugo jak plik testowy, nie jak pojedynczy test). */
export function zapomnijLimity(): void {
  pamiecLimitow = new WeakMap();
}

/**
 * Limity serwera, pobierane raz na zycie modulu. Zapamietujemy tez wynik "starszy serwer" (brak trasy sie nie
 * zmieni do odswiezenia strony), ale NIGDY bledu przejsciowego: zerwana siec nie moze udawac, ze serwer ma limit 200 MB.
 * Blad przejsciowy leci dalej, a wywolujacy decyduje, co pokazac (formularz: tekst bez liczby, wysylka: blad polaczenia).
 */
export function pobierzLimity(transport: TransportWgrywania = TRANSPORT_API): Promise<WynikLimitow> {
  const zapamietane = pamiecLimitow.get(transport);
  if (zapamietane) return zapamietane;

  const obietnica = (async (): Promise<WynikLimitow> => {
    let dto: LimityWgrywaniaDto;
    try {
      dto = await transport.limity();
    } catch (err) {
      if (brakTrasyLimitow(err)) return { zrodlo: "stary_serwer", limity: LIMITY_STAREGO_SERWERA };
      throw err;
    }
    const limity = limityZOdpowiedzi(dto);
    // Odpowiedz 200 o dziwnym ksztalcie to nie jest serwer, ktory zna ten kontrakt.
    if (!limity) return { zrodlo: "stary_serwer", limity: LIMITY_STAREGO_SERWERA };
    return { zrodlo: "serwer", limity };
  })();

  pamiecLimitow.set(transport, obietnica);
  obietnica.catch(() => {
    if (pamiecLimitow.get(transport) === obietnica) pamiecLimitow.delete(transport);
  });
  return obietnica;
}

// ---------------------------------------------------------------------------
// Bledy wlasne
// ---------------------------------------------------------------------------

/** Uzytkownik anulowal wysylanie (albo opuscil strone). To nie blad, tylko decyzja: UI nie pokazuje czerwonego komunikatu. */
export class AnulowanoWgrywanie extends Error {
  constructor() {
    super("Wysylanie anulowane");
    this.name = "AnulowanoWgrywanie";
  }
}

/**
 * Siec albo serwer nie odpowiadaly mimo wszystkich ponowien jednej porcji. Wgrywanie NIE jest kasowane na serwerze:
 * to godziny wysylki, a ponowne "Analizuj" z tym samym plikiem wznawia od miejsca, do ktorego dotarlo.
 * `wyslaneBajty` to liczba potwierdzona przez serwer (null: nie wiemy, wiec jej nie pokazujemy).
 */
export class BladPolaczenia extends Error {
  constructor(
    readonly proby: number,
    readonly przyczyna: unknown,
    readonly wyslaneBajty: number | null = null,
    readonly razemBajtow: number | null = null,
  ) {
    super(`Polaczenie przerwane po ${proby} ponowieniach`);
    this.name = "BladPolaczenia";
  }
}

/**
 * Wszystkie bajty doszly, ale nie wiemy, czy serwer zalozyl analize: polaczenie padlo albo nginx dal 502/503/504 w
 * trakcie skladania pliku. Analiza MOZE istniec, wiec UI nie moze kazac "wyslij jeszcze raz" bez zajrzenia na liste.
 */
export class BrakPotwierdzeniaPrzyjecia extends Error {
  constructor(readonly przyczyna: unknown) {
    super("Brak potwierdzenia przyjecia nagrania");
    this.name = "BrakPotwierdzeniaPrzyjecia";
  }
}

function wynikNiepewny(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true;
  return err.status === 408 || err.status === 502 || err.status === 503 || err.status === 504;
}

// ---------------------------------------------------------------------------
// Postep
// ---------------------------------------------------------------------------

export type FazaWgrywania =
  /** Zakladamy wgrywanie na serwerze (przed pierwsza porcja). */
  | "przygotowanie"
  /** Wysylamy porcje. */
  | "porcje"
  /** Wszystkie bajty u serwera: sklada plik, liczy sume kontrolna i sprawdza dlugosc. Anulowanie nie ma juz sensu. */
  | "skladanie"
  /** Stara sciezka: caly plik jednym zadaniem, bez mozliwosci zmierzenia postepu. */
  | "jedno_zadanie";

export interface PostepWgrywania {
  faza: FazaWgrywania;
  /** Bajty POTWIERDZONE przez serwer (nie te, ktore dopiero leca w trakcie porcji). */
  wyslaneBajty: number;
  razemBajtow: number;
  /** Numer porcji wysylanej teraz (od 1). 0 poza faza "porcje". */
  porcja: number;
  liczbaPorcji: number;
  /** null: za malo danych, zeby podac liczbe, ktorej mozna ufac. */
  predkoscBajtowNaSek: number | null;
  pozostaloSek: number | null;
  /**
   * Ustawione, gdy porcja sie nie udala i czekamy przed kolejna proba. Znika, gdy proba rusza (zdanie "ponawiam za N s"
   * nie moze wisiec przez caly czas ponowionej porcji, ktora na wolnym lacu trwa dziesiatki sekund).
   * `przyczyna` brak albo "siec": zerwane polaczenie lub blad serwera; "porcja_w_toku": serwer jeszcze zamyka poprzednie
   * polaczenie (409), wiec to czekanie na serwer, a nie awaria lacza.
   */
  ponowienie: { proba: number; maxProb: number; zaSek: number; przyczyna?: "siec" | "porcja_w_toku" } | null;
}

/** Po tylu ukonczonych porcjach i tylu milisekundach danych srednia jest warta pokazania. Ponizej: kreska, nie zgadywanie. */
export const MIN_PORCJI_DO_SZACUNKU = 3;
export const MIN_MS_DO_SZACUNKU = 10_000;

/**
 * Srednia predkosc od poczatku TEGO wysylania (razem z czasem czekania na ponowienia - uczciwa, nie optymistyczna) i
 * czas do konca przy tej predkosci. Bajty sprzed wznowienia nie licza sie jako "wyslane w tym biegu".
 */
export function szacujPredkosc(wejscie: {
  ukonczonePorcje: number;
  bajtyWTymBiegu: number;
  uplynelo: number;
  pozostaleBajty: number;
}): { predkoscBajtowNaSek: number | null; pozostaloSek: number | null } {
  const { ukonczonePorcje, bajtyWTymBiegu, uplynelo, pozostaleBajty } = wejscie;
  if (ukonczonePorcje < MIN_PORCJI_DO_SZACUNKU || uplynelo < MIN_MS_DO_SZACUNKU || bajtyWTymBiegu <= 0) {
    return { predkoscBajtowNaSek: null, pozostaloSek: null };
  }
  const predkosc = bajtyWTymBiegu / (uplynelo / 1000);
  return { predkoscBajtowNaSek: predkosc, pozostaloSek: Math.max(0, pozostaleBajty) / predkosc };
}

// ---------------------------------------------------------------------------
// Ponowienia
// ---------------------------------------------------------------------------

/** Odstepy przed kolejnymi ponowieniami jednej porcji: 5 prob po pierwszej, 1/2/4/8/16 s (lacznie 31 s). */
export const ODSTEPY_PONOWIEN_MS: readonly number[] = [1000, 2000, 4000, 8000, 16000];

/**
 * Odstepy przed kolejnymi ponowieniami, gdy serwer odpowiada 409 porcja_w_toku: 3/6/12/15/15/15/15 s (razem 81 s).
 * To blad CZASOWY: poprzednie polaczenie (np. po zmianie sieci) jeszcze trzyma blokade porcji, a serwer przejmuje ja po
 * 10 s bezczynnosci starego zapisu. Okno musi byc dluzsze od tego progu, inaczej klient poddalby sie, zanim serwer zwolni porcje.
 */
export const ODSTEPY_PORCJA_W_TOKU_MS: readonly number[] = [3000, 6000, 12000, 15000, 15000, 15000, 15000];

/** Ile razy z rzedu serwer moze nas odeslac do innego numeru porcji bez zadnego postepu, zanim uznamy to za blad. */
const MAX_PRZESKOKOW_BEZ_POSTEPU = 3;

function czyPrzerwano(err: unknown, signal: AbortSignal | undefined): boolean {
  if (signal?.aborted) return true;
  return err instanceof AnulowanoWgrywanie || (err instanceof Error && err.name === "AbortError");
}

/**
 * Czy porcje warto ponowic. Blad uzytkownika albo konfiguracji (400/401/403/404/413/429/507) nie przejdzie przy
 * kolejnej probie, wiec ponowienie tylko opoznialoby komunikat. Ponawiamy to, co moze byc chwilowe: brak sieci, 408,
 * 5xx (nginx 502/504 przy zrywanym laczu) i 409 porcja_w_toku (serwer jeszcze zapisuje poprzednia probe).
 */
function mozeBycChwilowy(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true;
  // 507 (brak miejsca na dysku serwera) to 5xx, ale NIE chwilowy: dysk sam sie nie zwolni w ciagu 31 s ponowien.
  if (err.status === 507) return false;
  if (err.status >= 500 || err.status === 408) return true;
  // 400 przerwane_polaczenie: serwer zobaczyl urwane cialo porcji i zdazyl odpowiedziec - to samo co zerwana siec.
  if (err.status === 400 && err.code === "przerwane_polaczenie") return true;
  return err.status === 409 && err.code === "porcja_w_toku";
}

function czyPorcjaWToku(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.code === "porcja_w_toku";
}

function liczbaCalkowita(wartosc: unknown): number | null {
  return typeof wartosc === "number" && Number.isInteger(wartosc) && wartosc >= 0 ? wartosc : null;
}

// ---------------------------------------------------------------------------
// Pamiec wgrywan: wznowienie po bledzie polaczenia
// ---------------------------------------------------------------------------

/**
 * Wgrywanie, ktore po bledzie zostalo na serwerze (godziny wysylki nie wolno wyrzucac). `odcisk` to pola startu
 * (nazwa, rozmiar, zgoda, opis, soczewki, dostawcy): serwer trzyma je od zalozenia wgrywania, wiec wznowienie jest
 * uczciwe tylko dla TYCH SAMYCH pol. Zmienione pola = nowe wgrywanie, a stare jest porzucane.
 */
interface ZapamietaneWgrywanie {
  uploadId: string;
  sizeBytes: number;
  odcisk: string;
}

let pamiecWgrywan = new WeakMap<File, ZapamietaneWgrywanie>();

/** Dla testow: modul zyje tak dlugo jak plik testowy, nie jak pojedynczy test. */
export function zapomnijWgrywania(): void {
  pamiecWgrywan = new WeakMap();
}

/** Czy dla tego pliku czeka nieskonczone wgrywanie (ponowne "Analizuj" je wznowi). */
export function maZapamietaneWgrywanie(plik: File): boolean {
  return pamiecWgrywan.has(plik);
}

/**
 * Porzuca zapamietane wgrywanie tego pliku: uzytkownik usunal plik, wybral inny albo wyszedl ze strony. Najlepsze
 * staranie, bez czekania na wynik (gdy sie nie uda, sprzata je petla retencji po UPLOAD_ABANDON_HOURS).
 */
export function porzucZapamietaneWgrywanie(plik: File, transport: TransportWgrywania = TRANSPORT_API): void {
  const zapamietane = pamiecWgrywan.get(plik);
  if (!zapamietane) return;
  pamiecWgrywan.delete(plik);
  void transport.anuluj(zapamietane.uploadId).catch(() => undefined);
}

/**
 * Po tych bledach wgrywania NIE kasujemy: ponowienie dalo ten sam wynik z powodu sieci, a nie tresci (BladPolaczenia),
 * albo to sesja wygasla (401) - po zalogowaniu plik jedzie dalej. Reszta (413, 415, 400, 507, ...) to odrzucenie
 * tresci: kolejna proba niczego nie zmieni, a plik tylko zajmuje dysk.
 */
function zachowajWgrywanie(err: unknown): boolean {
  return err instanceof BladPolaczenia || (err instanceof ApiError && err.status === 401);
}

export type CzekajFn = (ms: number, signal?: AbortSignal) => Promise<void>;

const czekajDomyslnie: CzekajFn = (ms, signal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new AnulowanoWgrywanie());
      return;
    }
    const przerwij = (): void => {
      clearTimeout(zegar);
      reject(new AnulowanoWgrywanie());
    };
    const zegar = setTimeout(() => {
      signal?.removeEventListener("abort", przerwij);
      resolve();
    }, ms);
    signal?.addEventListener("abort", przerwij, { once: true });
  });

// ---------------------------------------------------------------------------
// Wysylanie
// ---------------------------------------------------------------------------

export type PolaNagrania = Omit<WejscieNagrania, "file" | "rodzaj">;

export interface OpcjeWysylania {
  onPostep?: (postep: PostepWgrywania) => void;
  signal?: AbortSignal;
  transport?: TransportWgrywania;
  czekaj?: CzekajFn;
  /** Zegar w milisekundach (monotoniczny). Domyslnie performance.now(). */
  teraz?: () => number;
}

/** Pola formularza na cialo POST /api/sessions/uploads. Puste pola nie jadą: serwer traktuje brak jako "domyslnie". */
export function polaStartuZFormularza(plik: Pick<File, "name" | "type" | "size">, pola: PolaNagrania): PolaStartuWgrywania {
  const wynik: PolaStartuWgrywania = {
    fileName: plik.name,
    // Pusty typ MIME dostaje rzadkie rozszerzenie, ktorego przegladarka nie zna: rozstrzyga serwer, ktory zaglada do srodka.
    mimeType: plik.type || "application/octet-stream",
    sizeBytes: plik.size,
    // Serwer odrzuca wszystko poza prawdziwa zgoda; wysylamy to, co uzytkownik wybral.
    consent: pola.zgoda,
    lensSlugs: [...pola.soczewki],
    audioRetention: pola.audioRetention,
  };
  const tytul = pola.title.trim();
  if (tytul) wynik.title = tytul;
  const opis = pola.opis.trim();
  if (opis) wynik.context = opis;
  if (pola.sttProvider) wynik.sttProvider = pola.sttProvider;
  if (pola.expressionProvider) wynik.expressionProvider = pola.expressionProvider;
  if (pola.sttModel) wynik.sttModel = pola.sttModel;
  if (pola.expressionModel) wynik.expressionModel = pola.expressionModel;
  return wynik;
}

/**
 * Wysyla nagranie i zwraca odpowiedz przyjecia (202, to samo co przy tekscie).
 *
 * - Plik do `maxBajtowJednegoZadania` jedzie stara sciezka (api.upload), wiekszy porcjami: POST uploads, PUT porcji po
 *   kolei, POST complete.
 * - Kazda porcja ma do 5 ponowien (1/2/4/8/16 s), a 409 porcja_w_toku dluzsze okno (3/6/12/15/15/15/15 s). Przed
 *   ponowieniem pytamy serwer (GET uploads/:id), co NAPRAWDE dotarlo: zerwane polaczenie potrafi zgubic tylko
 *   odpowiedz, a porcja jest juz zapisana. Serwerowi ufamy bardziej niz sobie.
 * - Anulowanie (signal) przerywa fetch, a wgrywanie na serwerze jest porzucane (DELETE, najlepsze staranie: gdy sie nie
 *   uda, sprzata je petla retencji po UPLOAD_ABANDON_HOURS). Rzuca AnulowanoWgrywanie.
 * - Odrzucenie TRESCIOWE serwera (413, 415, 400, 507, ...) tez porzuca wgrywanie: ponowienie nic nie zmieni, a pliki
 *   zajmowalyby dysk. Natomiast po bledzie polaczenia (BladPolaczenia) i 401 wgrywania NIE kasujemy: to godziny wysylki.
 *   Zapamietujemy je dla tego samego obiektu File, a kolejne wywolanie z nim pyta serwer (GET) i wznawia od nextIndex.
 */
export async function wyslijNagranie(
  plik: File,
  pola: PolaNagrania,
  opcje: OpcjeWysylania = {},
): Promise<OdpowiedzPrzyjecia> {
  const transport = opcje.transport ?? TRANSPORT_API;
  const { signal } = opcje;

  rzucJesliPrzerwano(signal);
  const { limity } = await wykonajZPrzerwaniem(() => pobierzLimity(transport), signal);

  if (plik.size > limity.maxBajtowNagrania) {
    // Ta sama odpowiedz, ktora dalby serwer: jedno miejsce w interfejsie tlumaczy ja na zdanie.
    throw new ApiError(413, "plik_za_duzy", {
      maxMb: Math.round(limity.maxBajtowNagrania / 1024 / 1024),
      maxBajtow: limity.maxBajtowNagrania,
    });
  }

  const razem = plik.size;
  const wejscie: WejscieNagrania = { ...pola, rodzaj: "AUDIO", file: plik };

  if (plik.size <= limity.maxBajtowJednegoZadania) {
    opcje.onPostep?.({ ...POSTEP_PUSTY, faza: "jedno_zadanie", razemBajtow: razem });
    return wykonajZPrzerwaniem(() => transport.jednoZadanie(wejscie, signal), signal);
  }

  return wyslijPorcjami(plik, pola, transport, opcje);
}

/** Postep "nic jeszcze nie poszlo": baza dla kolejnych komunikatow, a w widoku stan od klikniecia do pierwszej odpowiedzi. */
export const POSTEP_PUSTY: PostepWgrywania = {
  faza: "przygotowanie",
  wyslaneBajty: 0,
  razemBajtow: 0,
  porcja: 0,
  liczbaPorcji: 0,
  predkoscBajtowNaSek: null,
  pozostaloSek: null,
  ponowienie: null,
};

function rzucJesliPrzerwano(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw new AnulowanoWgrywanie();
}

/** Przerwanie fetcha (DOMException AbortError) zamienia na jeden, wlasny typ bledu anulowania. */
async function wykonajZPrzerwaniem<T>(zadanie: () => Promise<T>, signal: AbortSignal | undefined): Promise<T> {
  try {
    return await zadanie();
  } catch (err) {
    if (czyPrzerwano(err, signal)) throw new AnulowanoWgrywanie();
    throw err;
  }
}

/** Wynik proby wznowienia: stan z serwera albo null (zaczynamy od nowa). */
type StanDoWznowienia = StanWgrywaniaDto | null;

/**
 * Czy zapamietane wgrywanie da sie wznowic. Pyta serwer (GET uploads/:id), bo tylko dysk serwera wie, co dotarlo.
 * - 404: serwer juz go nie pamieta, zaczynamy od nowa.
 * - inny rozmiar, podzial na porcje albo inne pola formularza: stare wgrywanie jest porzucane, zaczynamy od nowa.
 * - blad sieci: kilka ponowien jak przy porcji, a po nich BladPolaczenia (wgrywanie zostaje, mozna kliknac jeszcze raz).
 * - inny trwaly blad (np. 401): leci dalej, wgrywanie zostaje.
 */
async function sprobujWznowic(
  plik: File,
  odcisk: string,
  zapamietane: ZapamietaneWgrywanie,
  transport: TransportWgrywania,
  opcje: OpcjeWysylania,
  czekaj: CzekajFn,
  emituj: (czesc: Partial<PostepWgrywania>) => void,
): Promise<StanDoWznowienia> {
  const { signal } = opcje;
  const porzucStare = (): StanDoWznowienia => {
    pamiecWgrywan.delete(plik);
    void transport.anuluj(zapamietane.uploadId).catch(() => undefined);
    return null;
  };

  let proby = 0;
  for (;;) {
    let stan: StanWgrywaniaDto;
    try {
      stan = await wykonajZPrzerwaniem(() => transport.stan(zapamietane.uploadId, signal), signal);
    } catch (err) {
      if (err instanceof AnulowanoWgrywanie) {
        // Jawne anulowanie: tak jak w trakcie porcji, wgrywanie idzie do kosza.
        porzucStare();
        throw err;
      }
      if (err instanceof ApiError && err.status === 404) {
        pamiecWgrywan.delete(plik);
        return null;
      }
      if (!mozeBycChwilowy(err)) throw err;
      if (proby >= ODSTEPY_PONOWIEN_MS.length) throw new BladPolaczenia(proby, err);
      const odstep = ODSTEPY_PONOWIEN_MS[proby] as number;
      proby += 1;
      emituj({
        faza: "przygotowanie",
        ponowienie: { proba: proby, maxProb: ODSTEPY_PONOWIEN_MS.length, zaSek: odstep / 1000, przyczyna: "siec" },
      });
      try {
        await czekaj(odstep, signal);
        rzucJesliPrzerwano(signal);
      } catch (bladCzekania) {
        if (czyPrzerwano(bladCzekania, signal)) {
          porzucStare();
          throw new AnulowanoWgrywanie();
        }
        throw bladCzekania;
      }
      emituj({ faza: "przygotowanie" });
      continue;
    }

    const calkowite = liczbaCalkowita(stan.nextIndex);
    const porcjaBajtow = stan.chunkBytes;
    const zgodne =
      zapamietane.odcisk === odcisk &&
      stan.sizeBytes === plik.size &&
      dodatniaLiczba(porcjaBajtow) &&
      stan.totalChunks === Math.ceil(plik.size / porcjaBajtow) &&
      calkowite !== null &&
      calkowite <= stan.totalChunks;
    return zgodne ? stan : porzucStare();
  }
}

async function wyslijPorcjami(
  plik: File,
  pola: PolaNagrania,
  transport: TransportWgrywania,
  opcje: OpcjeWysylania,
): Promise<OdpowiedzPrzyjecia> {
  const { signal } = opcje;
  const teraz = opcje.teraz ?? ((): number => performance.now());
  const czekaj = opcje.czekaj ?? czekajDomyslnie;
  const razem = plik.size;

  const emituj = (czesc: Partial<PostepWgrywania>): void =>
    opcje.onPostep?.({ ...POSTEP_PUSTY, razemBajtow: razem, ...czesc });

  emituj({ faza: "przygotowanie" });

  // Ten sam plik po bledzie polaczenia: najpierw pytamy serwer, czy jego wgrywanie jeszcze jest, i wznawiamy od nextIndex.
  const odcisk = JSON.stringify(polaStartuZFormularza(plik, pola));
  const zapamietane = pamiecWgrywan.get(plik);
  const wznowione = zapamietane
    ? await sprobujWznowic(plik, odcisk, zapamietane, transport, opcje, czekaj, emituj)
    : null;

  let uploadId: string;
  let porcjaBajtow: number;
  let totalChunks: number;
  let indexStartowy = 0;
  let bajtySerwera = 0;
  if (wznowione && zapamietane) {
    uploadId = zapamietane.uploadId;
    porcjaBajtow = wznowione.chunkBytes;
    totalChunks = wznowione.totalChunks;
    indexStartowy = wznowione.nextIndex;
    bajtySerwera = Math.min(razem, liczbaCalkowita(wznowione.receivedBytes) ?? indexStartowy * porcjaBajtow);
  } else {
    const start = await wykonajZPrzerwaniem(() => transport.rozpocznij(polaStartuZFormularza(plik, pola), signal), signal);
    uploadId = start.uploadId;
    porcjaBajtow = start.chunkBytes;
    totalChunks = start.totalChunks;
    pamiecWgrywan.set(plik, { uploadId, sizeBytes: razem, odcisk });
  }

  // Sprzatanie po jawnym anulowaniu i po odrzuceniu tresciowym PRZED zlozeniem pliku. Nie czekamy na wynik: anulowanie
  // ma byc natychmiastowe, a niepowodzenie sprzatania (np. brak sieci) nie jest problemem uzytkownika - porzucone
  // wgrywanie zbiera petla retencji.
  const porzuc = (): void => {
    pamiecWgrywan.delete(plik);
    void transport.anuluj(uploadId).catch(() => undefined);
  };

  try {
    const liczbaPorcji = Math.ceil(razem / porcjaBajtow);
    // Serwer jest zrodlem prawdy o podziale, ale rozjazd z naszym rachunkiem to blad protokolu, nie cos do obejscia.
    if (!dodatniaLiczba(porcjaBajtow) || totalChunks !== liczbaPorcji) {
      throw new ApiError(502, "niezgodna_odpowiedz_serwera", {
        chunkBytes: porcjaBajtow,
        totalChunks,
      });
    }

    const potwierdzone = (index: number): number => Math.min(razem, index * porcjaBajtow);

    let index = indexStartowy;
    // Ustawiane przy pierwszej porcji: od tej chwili liczymy srednia predkosc.
    let poczatekMs: number | null = null;
    let bajtyNaStarcie = 0;
    let ukonczonePorcje = 0;
    let przeskokiBezPostepu = 0;

    const postepPorcji = (czesc: Partial<PostepWgrywania> = {}): PostepWgrywania => {
      const wyslane = potwierdzone(index);
      const szacunek = szacujPredkosc({
        ukonczonePorcje,
        bajtyWTymBiegu: wyslane - bajtyNaStarcie,
        uplynelo: poczatekMs === null ? 0 : teraz() - poczatekMs,
        pozostaleBajty: razem - wyslane,
      });
      return {
        ...POSTEP_PUSTY,
        faza: "porcje",
        razemBajtow: razem,
        wyslaneBajty: wyslane,
        porcja: Math.min(index + 1, liczbaPorcji),
        liczbaPorcji,
        ...szacunek,
        ponowienie: null,
        ...czesc,
      };
    };

    while (index < liczbaPorcji) {
      rzucJesliPrzerwano(signal);
      if (poczatekMs === null) {
        poczatekMs = teraz();
        bajtyNaStarcie = potwierdzone(index);
      }
      opcje.onPostep?.(postepPorcji());

      const od = index * porcjaBajtow;
      const dane = plik.slice(od, Math.min(razem, od + porcjaBajtow));
      // Osobne budzety: zerwana siec (1/2/4/8/16 s) i 409 porcja_w_toku (3/6/12/15/15/15/15 s) nie zjadaja sobie prob.
      let ponowien = 0;
      let ponowienWToku = 0;

      // Jedna porcja z ponowieniami. Wychodzi albo z nowym `index` (sukces, wznowienie, przeskok), albo wyjatkiem.
      for (;;) {
        try {
          const odpowiedz = await transport.porcja(uploadId, index, dane, signal);
          const nastepny = liczbaCalkowita(odpowiedz.nextIndex);
          if (nastepny === null || nastepny <= index) {
            throw new ApiError(502, "niezgodna_odpowiedz_serwera", { nextIndex: odpowiedz.nextIndex });
          }
          ukonczonePorcje += 1;
          przeskokiBezPostepu = 0;
          index = Math.min(nastepny, liczbaPorcji);
          bajtySerwera = Math.min(razem, liczbaCalkowita(odpowiedz.receivedBytes) ?? potwierdzone(index));
          break;
        } catch (err) {
          if (czyPrzerwano(err, signal)) throw new AnulowanoWgrywanie();

          // Serwer mowi, od ktorej porcji chce: przeskakujemy. Zapetlenie bez postepu to blad, nie wieczne przeskakiwanie.
          if (err instanceof ApiError && err.status === 409 && err.code === "zly_numer_porcji") {
            const oczekiwany = liczbaCalkowita(err.szczegoly.nextIndex);
            przeskokiBezPostepu += 1;
            if (oczekiwany === null || oczekiwany > liczbaPorcji || przeskokiBezPostepu > MAX_PRZESKOKOW_BEZ_POSTEPU) {
              throw err;
            }
            index = oczekiwany;
            bajtySerwera = potwierdzone(index);
            break;
          }

          if (!mozeBycChwilowy(err)) throw err;
          const wToku = czyPorcjaWToku(err);
          const odstepy = wToku ? ODSTEPY_PORCJA_W_TOKU_MS : ODSTEPY_PONOWIEN_MS;
          const uzyte = wToku ? ponowienWToku : ponowien;
          if (uzyte >= odstepy.length) throw new BladPolaczenia(uzyte, err);

          const odstep = odstepy[uzyte] as number;
          if (wToku) ponowienWToku += 1;
          else ponowien += 1;
          opcje.onPostep?.(
            postepPorcji({
              ponowienie: {
                proba: uzyte + 1,
                maxProb: odstepy.length,
                zaSek: odstep / 1000,
                przyczyna: wToku ? "porcja_w_toku" : "siec",
              },
            }),
          );
          await czekaj(odstep, signal);
          rzucJesliPrzerwano(signal);

          // Wznowienie: co serwer ma NAPRAWDE. Odpowiedz moglo zgubic lacze, a porcja leży juz na dysku.
          try {
            const stan = await transport.stan(uploadId, signal);
            const maSerwer = liczbaCalkowita(stan.nextIndex);
            if (maSerwer !== null && maSerwer !== index && maSerwer <= liczbaPorcji) {
              index = maSerwer;
              bajtySerwera = Math.min(razem, liczbaCalkowita(stan.receivedBytes) ?? potwierdzone(index));
              break;
            }
          } catch (blad) {
            if (czyPrzerwano(blad, signal)) throw new AnulowanoWgrywanie();
            // Wgrywanie zniknelo z serwera (404) albo to inny trwaly blad: dalsze porcje nic nie dadza.
            if (!mozeBycChwilowy(blad)) throw blad;
            // Chwilowy blad samego pytania: ponawiamy porcje, a ponowienia dalej sie licza.
          }

          // Proba rusza: zdanie "ponawiam za N s" przestaje byc prawdziwe, wiec znika (a nie wisi przez caly PUT porcji).
          opcje.onPostep?.(postepPorcji());
        }
      }
    }

    // Wszystkie bajty u serwera. Od teraz zlozenie i sprawdzenie pliku moze trwac minuty (suma kontrolna 3 GiB, ffprobe).
    rzucJesliPrzerwano(signal);
    opcje.onPostep?.(postepPorcji({ faza: "skladanie", wyslaneBajty: razem, porcja: 0 }));
  } catch (err) {
    // Siec (BladPolaczenia) i wygasla sesja (401): wgrywanie zostaje na serwerze, a plik pamietamy - kolejne "Analizuj"
    // z tym samym plikiem wznowi od miejsca, do ktorego dotarlo. Liczby w komunikacie pochodza z odpowiedzi serwera.
    if (zachowajWgrywanie(err)) {
      throw err instanceof BladPolaczenia ? new BladPolaczenia(err.proby, err.przyczyna, bajtySerwera, razem) : err;
    }
    porzuc();
    throw err;
  }

  // Zlozenie NIE jest w bloku sprzatajacym i nie dostaje sygnalu anulowania, z dwoch powodow. Po timeoucie nginxa
  // (502/504) serwer moze jeszcze dokonczyc zakladanie analizy, wiec skasowanie wgrywania mogloby mu wyciac plik spod
  // rak. A przerwane zadanie "complete" nie mowi nic o tym, czy analiza powstala. Nie ponawiamy tez zlozenia: nie jest
  // idempotentne i drugi strzal zalozylby druga analize.
  try {
    const przyjecie = await transport.zakoncz(uploadId);
    pamiecWgrywan.delete(plik);
    return przyjecie;
  } catch (err) {
    // Brakujace bajty (409 niekompletny_plik): wgrywanie zostaje, wiec ponowne "Analizuj" dosle reszte. Przy kazdym
    // innym wyniku serwer albo zalozyl analize, albo sprzatnal plik, albo nie wiemy - pamiec tego wgrywania nie ma sensu.
    if (!(err instanceof ApiError && err.status === 409 && err.code === "niekompletny_plik")) pamiecWgrywan.delete(plik);
    if (wynikNiepewny(err)) throw new BrakPotwierdzeniaPrzyjecia(err);
    throw err;
  }
}
